import { isString } from "./primitive-types.js";
// This file is purposefully updated by hand to remove the reliance on a compilation step for other libs.

import type { TypeGuardFn } from "./TypeGuardFn.js";

/** Type: Extract the type of elements in the array */
export type ArrayElement<A> = A extends readonly (infer T)[] ? T : never;

/** Type: An object that is not an array */
export type NotArray = { length?: never };

/** Type: An array with a single item */
export type SingleItemArray<T> = [ T ];

/** Type: An array with a multiple items */
export type MultiItemArray<T> = [ T, T, ...T[] ];

/** Type: Either ReadonlyArray<T> or Array<T> */
export type MutableOrImmutableArray<T> = T[] | readonly T[];

/** Type: Converts readonly arrays into mutable arrays */
export type AsMutableArray<T extends MutableOrImmutableArray<any>> =
    T extends readonly any[]
    ? (
        T extends readonly []
        ? [] :
        T extends readonly [ infer First, ...infer Remaining ]
        ? [ First, ...Remaining ] :
        T extends readonly (infer U)[]
        ? U[] :
        never
    ) :
    T;

/** Type: Converts readonly arrays into mutable arrays */
export type AsImmutableArray<T extends MutableOrImmutableArray<any>> =
    T extends readonly any[]
    ? T :
    T extends []
    ? readonly [] :
    T extends [ infer First, ...infer Remaining ]
    ? readonly [ First, ...Remaining ] :
    T extends (infer U)[] ? U[]
    : never;


export type MatchMutableOrImmutable<TSource extends MutableOrImmutableArray<any>, TArray extends MutableOrImmutableArray<any>> =
    TSource extends readonly any[]
    ? (TArray extends readonly any[] ? TArray : AsImmutableArray<TArray>)
    : (TArray extends readonly any[] ? [ ...TArray ] : AsMutableArray<TArray>);

/** Type: Cast array to MutableOrImmutableArray<T> */
export type ToMutableOrImmutableArray<T extends any[]>
    = T extends [ infer F, ...infer R ]
    ? [ F, ...R ] | readonly [ F, ...R ] :
    T extends []
    ? [] | readonly [] :
    T extends (infer U)[]
    ? MutableOrImmutableArray<U>
    : never;


// export type AsMutableArray<T extends MutableOrImmutableArray<any>> =
//   T extends readonly []
//   ? [] :
//   T extends []
//   ? [] :
//   T extends readonly [infer First, ...infer Remaining]
//   ? [First, ...Remaining] :
//   T extends [infer First, ...infer Remaining]
//   ? [First, ...Remaining] :
//   T extends readonly (infer U)[]
//   ? U[] :
//   T extends (infer U)[]
//   ? U[] :
//   never;




/** Type Guard: Checks if a value is an array */
export function isArray (value: unknown): value is unknown[] | readonly unknown[] {
    return Array.isArray(value);
}

/** Type Guard: Checks if a value is NOT an array */
export function isNotArray (value: unknown): value is NotArray {
    return isArray(value) === false;
}

/** Type Guard: Checks if a value is an array with a single item */
export function isSingleItemArray<T> (
    value: unknown
): value is SingleItemArray<T> {
    return isArray(value) && value.length === 1;
}

/** Type Guard: Checks if a value is an array with multiple items */
export function isMultiItemArray<T> (value: unknown): value is MultiItemArray<T> {
    return isArray(value) && value.length > 1;
}

/** Type Guard: Checks if a value is an array with all items being of type T */
export function isArrayOfType<T> (
    value: unknown,
    typeCheckFunc: TypeGuardFn<T>
): value is T[] | readonly T[];

/** Type Guard: Checks if a value is an array with all items being of type T */
export function isArrayOfType<T> (
    value: unknown,
    typeCheckFunc: (val: unknown) => boolean
): value is T[] | readonly T[];
export function isArrayOfType<T> (
    value: unknown,
    typeCheckFunc: TypeGuardFn<T> | ((val: unknown) => boolean)
): value is T[] | readonly T[]
{
    return isArray(value) && value.every(typeCheckFunc) === true;
}
/** Type Guard: Checks if a value is an array of strings */
export function isStringArray (value: unknown): value is string[] | readonly string[] {
    return isArrayOfType(value, isString);
}